import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useWorkflow } from '../contexts/WorkflowContext.jsx';
import DeployChat from '../components/deploy/DeployChat.jsx';
import DeployPreview from '../components/deploy/DeployPreview.jsx';
import CloudflareDeployWizard from '../components/CloudflareDeployWizard.jsx';
import api from '../utils/api.js';
import { API_CONFIG } from '../config/api.js';
import { createLogger } from '../utils/logger.js';
import {
  Container,
  Card,
  Button,
  Heading,
  Text,
  Spinner,
  Alert,
  Icon,
  Badge,
  MobileBottomNav,
  LogoText
} from '../components/ui/index.jsx';
import UserMenu from '../components/UserMenu.jsx';

const log = createLogger('VibeCoding');

export default function VibeCoding() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, authType } = useAuth();

  // WorkflowContext - use vibe-specific methods
  const {
    workflowState,
    sendVibeStart,
    sendVibeChat,
    sendVibeStop,
    sendToggleVisibility,
    sendVibePushToGitHub,
    startDeployment,
    clearWorkflow,
  } = useWorkflow();

  // Extract state
  const deployment = workflowState.deployment || {};
  const isConnected = workflowState.wsConnected;
  const previewUrl = deployment.previewUrl;
  const isPublic = deployment.isPublic || false;
  const buildLogs = deployment.buildLogs || [];
  const deployError = deployment.error;
  const chatHistory = deployment.chatHistory || [];
  const githubUrl = deployment.githubUrl;
  const pushingToGitHub = deployment.pushingToGitHub;

  // Local state
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVibeStarted, setIsVibeStarted] = useState(false);
  const [initialIdea, setInitialIdea] = useState('');
  const [showDeployWizard, setShowDeployWizard] = useState(false);
  const [infraStatus, setInfraStatus] = useState({ ready: false });
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [showBuildLogs, setShowBuildLogs] = useState(false);

  // GitHub connection state
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubUser, setGithubUser] = useState(null);
  const [githubLoading, setGithubLoading] = useState(true);
  const [repoName, setRepoName] = useState('');
  const [showRepoInput, setShowRepoInput] = useState(false);

  // Preview viewport mode
  const [viewportMode, setViewportMode] = useState('desktop'); // 'desktop' | 'mobile'

  // Handle visibility toggle
  const handleToggleVisibility = useCallback(() => {
    if (!previewUrl || isTogglingVisibility) return;
    setIsTogglingVisibility(true);
    sendToggleVisibility(!isPublic, sessionId);
    setTimeout(() => setIsTogglingVisibility(false), 2000);
  }, [previewUrl, isPublic, sessionId, sendToggleVisibility, isTogglingVisibility]);

  // Handle push to GitHub
  const handlePushToGitHub = useCallback(() => {
    if (!repoName.trim()) return;
    sendVibePushToGitHub(repoName.trim(), true);
    setShowRepoInput(false);
  }, [sendVibePushToGitHub, repoName]);

  // Handle GitHub connect - redirect to OAuth
  const handleGitHubConnect = useCallback(() => {
    const returnUrl = encodeURIComponent(`/vibe/${sessionId}`);
    window.location.href = `${API_CONFIG.endpoints.api.deploy.github.oauth}?return_url=${returnUrl}`;
  }, [sessionId]);

  // Generate default repo name
  const generateDefaultRepoName = useCallback(() => {
    const ideaSlug = initialIdea
      ? initialIdea.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)
      : 'vibe-app';
    return `${ideaSlug}-${Date.now().toString(36).slice(-4)}`;
  }, [initialIdea]);

  // Derive isGenerating from workflow state
  const isGenerating = isVibeStarted && !previewUrl && deployment.status !== 'error';

  // Mobile nav - keep for mobile users even though desktop is workbench
  const mobileNavItems = useMemo(() => [
    { id: 'home', icon: 'home', label: 'Home', path: '/', onClick: () => navigate('/') },
    { id: 'dashboard', icon: 'layout-dashboard', label: 'Dashboard', path: '/dashboard', onClick: () => navigate('/dashboard') },
    { id: 'vibe', icon: 'code', label: 'Vibe', path: `/vibe/${sessionId}`, active: true }
  ], [navigate, sessionId]);

  // Load session data on mount
  useEffect(() => {
    async function loadSession() {
      if (!sessionId || sessionId === 'new') return;
      try {
        const response = await api.getSession(sessionId);
        if (response?.session?.idea) {
          setInitialIdea(response.session.idea);
          log.log('Loaded session idea:', response.session.idea);
        }
      } catch (error) {
        log.error('Failed to load session:', error);
      }
    }
    loadSession();
  }, [sessionId]);

  // Check GitHub connection status
  useEffect(() => {
    async function checkGitHubConnection() {
      try {
        setGithubLoading(true);
        const response = await api.request('/api/v2/deploy/connection-status', { method: 'GET' });
        if (response.github?.connected) {
          setGithubConnected(true);
          setGithubUser(response.github.user);
        } else {
          setGithubConnected(false);
          setGithubUser(null);
        }
      } catch (error) {
        setGithubConnected(false);
      } finally {
        setGithubLoading(false);
      }
    }
    checkGitHubConnection();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('github_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Auto-start vibe when we have an idea and WebSocket is connected
  useEffect(() => {
    if (initialIdea && isConnected && !isVibeStarted) {
      log.log('Sending start_vibe (backend detects new vs resume)');
      setIsVibeStarted(true);
      sendVibeStart(initialIdea, sessionId);
    }
  }, [initialIdea, isConnected, isVibeStarted, sendVibeStart, sessionId]);

  // Initialize WebSocket connection for vibe session
  useEffect(() => {
    if (sessionId && sessionId !== 'new' && user?.id) {
      const agentId = `architect_${user.id}_${sessionId}`;
      if (workflowState.agentId !== agentId) {
        log.log('Initializing vibe WebSocket connection:', agentId);
        if (workflowState.agentId) clearWorkflow();
        startDeployment({
          deploymentId: sessionId,
          agentId,
          sessionId,
          status: 'initialized',
          phase: 'vibe',
        });
      }
    }
  }, [sessionId, user?.id, workflowState.agentId, startDeployment, clearWorkflow]);

  // Start vibe coding session
  const handleStartVibe = useCallback(async () => {
    if (!chatInput.trim() && !initialIdea) return;
    const idea = chatInput.trim() || initialIdea;
    setIsSending(true);
    setIsVibeStarted(true);
    try {
      sendVibeStart(idea, sessionId);
      setChatInput('');
    } catch (error) {
      log.error('Failed to start vibe:', error);
      window.showToast?.('Failed to start. Please try again.', 'error');
    } finally {
      setIsSending(false);
    }
  }, [chatInput, initialIdea, sendVibeStart, sessionId]);

  // Handle chat submit
  const handleChatSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || isSending) return;
    setIsSending(true);
    try {
      sendVibeChat(chatInput.trim(), sessionId);
      setChatInput('');
    } catch (error) {
      window.showToast?.('Failed to send message.', 'error');
    } finally {
      setIsSending(false);
    }
  }, [chatInput, isSending, sendVibeChat, sessionId]);

  // Placeholder for handleClearChat - assuming it will be defined elsewhere or added by user
  const handleClearChat = useCallback(() => {
    // Implement chat clearing logic here
    console.log('Clear chat clicked');
    // Example: clearWorkflow(); // if clearWorkflow also clears chat history
    // Or: setChatHistory([]); // if chatHistory is local state
  }, []);

  // Chat messages mapping
  const messages = chatHistory.map((msg, i) => ({
    id: i,
    role: msg.role || 'user',
    content: msg.content,
    timestamp: msg.timestamp,
    file: msg.file,
    previewUrl: msg.previewUrl,
    error: msg.error
  }));

  // Determine connection status for the badge
  const status = isConnected ? 'connected' : 'connecting'; // Simplified for the badge

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user || authType !== 'authenticated') {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navigation />
        <Container className="py-8">
          <Alert variant="warning">
            <Text>Please sign in to use Vibe Coding.</Text>
            <Button variant="primary" size="sm" onClick={() => navigate('/signin')} className="mt-2">Sign In</Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#050505] overflow-hidden flex flex-col text-white selection:bg-[#7B61FF]/30">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-[#7B61FF]/5 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[800px] h-[800px] bg-[#06B6D4]/5 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <header className="h-14 border-b border-white/5 bg-[#0A0A0A]/50 backdrop-blur-md flex items-center justify-between px-6 z-20 shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4">
            {/* Logo / Home Link - Same pattern as main Navigation */}
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-3">
              <LogoText size="sm" />
              <div className="h-5 w-px bg-white/10" />
              <h1 className="text-sm font-bold text-white tracking-wide">VIBE CODING</h1>
            </div>

            {/* Connection Status */}
            <div className="flex items-center">
              {(status === 'connected' || isConnected) ? (
                <Badge variant="success" size="xs" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 backdrop-blur-md">
                  Online
                </Badge>
              ) : (
                <Badge variant="warning" size="xs" className="bg-amber-500/10 text-amber-500 border-amber-500/20 backdrop-blur-md">
                  {status}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Controls */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearChat}
            className="text-gray-400 hover:text-white hover:bg-white/5 hidden sm:flex"
            title="Clear Chat"
          >
            <Icon name="trash-2" size="xs" className="mr-2" />
            Clear
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowDeployWizard(true)}
            className="bg-[#7B61FF] hover:bg-[#8B71FF] text-white shadow-lg shadow-[#7B61FF]/20 border border-[#7B61FF]/50"
          >
            <Icon name="rocket" size="xs" className="mr-2" />
            Deploy
          </Button>

          <div className="h-5 w-px bg-white/10 mx-1" />

          {/* User Nav */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white hidden md:flex"
          >
            Dashboard
          </Button>

          <UserMenu />
        </div>
      </header>

      {/* Main Workbench Area */}
      <main className="flex-1 flex overflow-hidden relative z-10">

        {/* Left Panel: Chat (400px fixed or 30%) */}
        <div className="w-[420px] flex flex-col border-r border-white/5 bg-[#050505]/30 backdrop-blur-sm shrink-0">
          {/* Chat Header */}
          <div className="h-12 border-b border-white/5 flex items-center px-4 justify-between bg-[#0A0A0A]/50">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Architect Chat</span>
            {deployment.status === 'building' && (
              <span className="text-xs text-[#06B6D4] flex items-center gap-1">
                <Spinner size="xs" />
                Building...
              </span>
            )}
          </div>

          {/* Messages Area */}
          <div className="flex-1 min-h-0 overflow-hidden relative">
            {/* Empty State Overlay */}
            {!isVibeStarted && messages.length === 0 && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center bg-[#050505]/80 pointer-events-none">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06B6D4]/20 to-transparent border border-[#06B6D4]/20 flex items-center justify-center mb-4">
                  <Icon name="sparkles" size="xl" className="text-[#06B6D4]" />
                </div>
                <h3 className="text-white font-medium mb-2">What shall we build?</h3>
                <p className="text-sm text-gray-500">Describe your idea, and I'll code it live.</p>
              </div>
            )}

            <DeployChat messages={messages} isLoading={isSending} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl">
            <form onSubmit={!isVibeStarted ? (e) => { e.preventDefault(); handleStartVibe(); } : handleChatSubmit}>
              <div className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r from-[#06B6D4] to-[#7B61FF] rounded-xl opacity-0 group-focus-within:opacity-20 transition duration-500 blur`}></div>
                <div className="relative flex gap-2 bg-[#121212] border border-white/10 rounded-xl p-2 focus-within:border-white/20 transition-colors">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={!isConnected ? "Connecting..." : (!isVibeStarted ? "e.g., A landing page for a coffee shop..." : "Ask for changes...")}
                    className="flex-1 bg-transparent text-sm text-white px-3 py-2 focus:outline-none placeholder-gray-600"
                    disabled={isGenerating || !isConnected}
                  />
                  {isGenerating ? (
                    <button
                      type="button"
                      onClick={() => { sendVibeStop?.(); setIsVibeStarted(false); setIsSending(false); }}
                      className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                    >
                      <Icon name="square" size="xs" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || !isConnected}
                      className="p-2 rounded-lg bg-[#06B6D4]/10 text-[#06B6D4] hover:bg-[#06B6D4]/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      {!isConnected ? <Spinner size="xs" /> : <Icon name="arrow-up" size="xs" />}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Right Panel: Preview Area (Flex 1) */}
        <div className="flex-1 flex flex-col bg-[#080808] relative overflow-hidden">
          {/* Toolbar */}
          <div className="h-12 border-b border-white/5 flex items-center justify-between px-4 bg-[#0A0A0A]/50">
            <div className="flex items-center gap-1 bg-black/40 p-1 rounded-lg border border-white/5">
              <button
                onClick={() => setViewportMode('desktop')}
                className={`p-1.5 rounded transition-all ${viewportMode === 'desktop' ? 'bg-[#1A1A1A] text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <Icon name="monitor" size="xs" />
              </button>
              <button
                onClick={() => setViewportMode('mobile')}
                className={`p-1.5 rounded transition-all ${viewportMode === 'mobile' ? 'bg-[#1A1A1A] text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <Icon name="smartphone" size="xs" />
              </button>
            </div>

            {previewUrl ? (
              <div className="flex items-center gap-2">
                {/* Status/Actions for preview */}
                <div className="flex items-center bg-black/40 rounded-lg border border-white/5 px-3 py-1.5 gap-3">
                  {/* Public/Private Toggle - Improved Visibility */}
                  <button
                    onClick={handleToggleVisibility}
                    disabled={isTogglingVisibility}
                    className={`
                      flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium transition-all
                      ${isPublic
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                        : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10 hover:text-white'}
                    `}
                  >
                    {isTogglingVisibility ? (
                      <Spinner size="xs" />
                    ) : (
                      <Icon name={isPublic ? 'globe' : 'lock'} size="xs" />
                    )}
                    <span>{isPublic ? 'Public' : 'Make Public'}</span>
                  </button>

                  <div className="w-px h-3 bg-white/10" />

                  {/* GitHub */}
                  {githubUrl ? (
                    <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white">
                      <Icon name="brand-github" size="xs" />
                    </a>
                  ) : (
                    <button onClick={() => setShowRepoInput(!showRepoInput)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white">
                      <Icon name="brand-github" size="xs" />
                    </button>
                  )}
                </div>

                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Icon name="external-link" size="xs" />
                </a>
              </div>
            ) : (
              <span className="text-xs text-gray-600">Preview Standby</span>
            )}
          </div>

          {/* Repo Input Popup */}
          {showRepoInput && (
            <div className="absolute top-14 right-4 z-30 p-3 bg-[#1A1A1A] border border-white/10 rounded-xl shadow-xl flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
              <input
                type="text"
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                placeholder="repo-name"
                className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#7B61FF]"
              />
              <button onClick={handlePushToGitHub} disabled={pushingToGitHub} className="p-1 text-purple-400 hover:text-purple-300">
                {pushingToGitHub ? <Spinner size="xs" /> : <Icon name="check" size="xs" />}
              </button>
              <button onClick={() => setShowRepoInput(false)} className="p-1 text-gray-500 hover:text-gray-300">
                <Icon name="x" size="xs" />
              </button>
            </div>
          )}

          {/* Preview Container */}
          <div className="flex-1 flex items-center justify-center bg-[#070709] relative p-4">
            {/* Background Grid Pattern */}
            <div className="absolute inset-0 opacity-[0.03]"
              style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
            </div>

            <div className={`relative transition-all duration-500 ease-in-out flex flex-col shadow-2xl ${viewportMode === 'mobile' ? 'w-[375px] h-[750px] max-h-full' : 'w-full h-full'
              }`}>
              <DeployPreview
                previewUrl={previewUrl}
                phase={deployment.status} // idle, generating, building, ready, error
                previewVersion={deployment.previewVersion}
                viewportMode={viewportMode}
              />
            </div>
          </div>
        </div>

      </main>

      {/* Deploy Wizard Modal (Fixed Overlay) */}
      {showDeployWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-[#0F0F12] border border-white/10 rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading level={3} className="text-white">Deploy to Edge</Heading>
              <Button variant="ghost" size="sm" onClick={() => setShowDeployWizard(false)}>
                <Icon name="x" size="sm" />
              </Button>
            </div>
            <CloudflareDeployWizard
              onStatusChange={setInfraStatus}
              sessionId={sessionId}
            />
          </div>
        </div>
      )}

      {/* Error Toast */}
      {deployError && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-200 rounded-full text-sm backdrop-blur-md z-50">
          {deployError}
        </div>
      )}
    </div>
  );
}
